import {
  DISCOVER_GUEST_STATS,
  GuestStat,
} from '../pages/discover/discover-guest-landing.const';

/**
 * Content for the first-run welcome carousel — the four slides a brand-new user
 * sees before they've done anything, explaining what Programs / Library /
 * Discover are for.
 *
 * Single source of truth for the copy (shared-string rule). The catalog figures
 * are **imported** from `discover-guest-landing.const.ts` rather than restated,
 * so a reseed updates the guest landing and this carousel together.
 *
 * Hero images are the public, watermarked catalog card art (the same masters the
 * landing site serves from `landing/public/catalog/`), re-encoded to 720px webp
 * into `assets/onboarding/`. Deliberately NOT app screenshots: screenshots go
 * stale the moment the tab bar or theme changes (the checked-in store shots
 * still show a retired 5-tab green build), and they show empty first-run states,
 * which is exactly what we're trying to explain away. Photography of the real
 * catalog stays true regardless of UI changes — and it shows a guest the
 * content they can't otherwise see behind the auth gate.
 */

/** One carousel slide. */
export interface OnboardingSlide {
  /** Uppercase kicker above the title. */
  eyebrow: string;
  /** Title, split so the tail can be accented in the brand colour. */
  titleLead: string;
  titleAccent: string;
  /** Body copy — one or two sentences, no jargon. */
  body: string;
  /** Hero image path under assets/. */
  image: string;
  /** Alt text for the hero. */
  imageAlt: string;
  /** Optional number tiles under the body. */
  stats?: readonly GuestStat[];
}

export const ONBOARDING_SLIDES: readonly OnboardingSlide[] = [
  {
    eyebrow: 'Welcome to Sequal',
    titleLead: 'Every workout. Every body. ',
    titleAccent: 'One library.',
    body: 'Press play on ready-made plans, or build your own from thousands of animated movements. Here’s the quick version — it takes about 20 seconds.',
    image: 'assets/onboarding/welcome.webp',
    imageAlt: 'A group moving through a yoga flow together',
  },
  {
    eyebrow: 'Programs · Plan your week',
    titleLead: 'Plan your week, ',
    titleAccent: 'then press play.',
    body: 'A program is a multi-day plan — the days you train, and what you do on each. Start from a ready-made one or build your own from your Library.',
    // Image swapped with the Library slide below (author preference).
    image: 'assets/onboarding/library.webp',
    imageAlt: 'A person performing an upper-body pull exercise',
  },
  {
    eyebrow: 'Library · Your starting tab',
    titleLead: 'Your exercises, circuits ',
    titleAccent: '& workouts.',
    body: 'Anything you create or save from the catalog lands here, ready to drop into a program. Your own photos and video are welcome too — this is your home base.',
    // Image swapped with the Programs slide above (author preference).
    image: 'assets/onboarding/programs.webp',
    imageAlt: 'A person training with dumbbells in a gym',
  },
  {
    eyebrow: 'Discover · The catalog',
    titleLead: 'Thousands of moves, ',
    titleAccent: 'shown in 3D.',
    body: 'Browse the full catalog of exercises, circuits and ready-made programs — strength, yoga and cardio — each animated so you can see the muscles it works. Save any of it in a tap.',
    image: 'assets/onboarding/discover.webp',
    imageAlt: 'A person holding a seated yoga stretch',
    // Exercise-library figures only. The ready-made plans that used to fill the
    // third tile are AI-authored general templates — describing them is honest,
    // counting them on a welcome screen is a boast (see the guest-landing const).
    stats: [
      DISCOVER_GUEST_STATS[1],
      DISCOVER_GUEST_STATS[0],
      DISCOVER_GUEST_STATS[2],
    ],
  },
];

/** Button copy — shared so the modal and its spec can't drift. */
export enum OnboardingCta {
  Skip = 'Skip',
  Next = 'Next',
  StartTour = 'Show me around',
  Explore = 'I’ll explore on my own',
}

/** Menu entry that replays the carousel. */
export const ONBOARDING_MENU_LABEL = 'Getting started';

/** Menu entry that opens the guided-tour replay chooser. */
export const GUIDED_TOURS_MENU_LABEL = 'App tours';

/** Header of the guided-tour chooser action sheet. */
export const GUIDED_TOURS_SHEET_HEADER = 'Replay a tour';
